import type App from './App';
import type { AppProps } from './App';
import type { UiConfig } from './utils/DefaultConfig';

import i18next from 'i18next';
import merge from 'lodash/merge';
import { createRoot } from 'react-dom/client';

import { AppLoader } from './AppLoader';
import { ALLOW_CUSTOM_POOL, GATEKEEPER_ID } from './Constants';
import { DisableCloud, EaiwsStartup } from './DefaultValues';
import { SessionManager as PconUiSessionManager } from './managers/SessionManager';
import { loadUiSettings, sendParams } from './utils/UiSettings';

import { addGatekeeperIdHook } from '@egr/xbox/base-app/hooks/GatekeeperId';
import { initInstance, USER_LANGUAGES } from '@egr/xbox/base-app/i18n';
import { GatekeeperManager } from '@egr/xbox/base-app/managers/GatekeeperManager';
import { StaticEaiwsManager } from '@egr/xbox/base-app/managers/StaticEaiwsServer';
import { pConServiceProvider } from '@egr/xbox/pcon-login-api/ServicesProvider';
import { EAIWS_SERVER } from '@egr/xbox/utils/Constants';
import { printTimer } from '@egr/xbox/utils/Debug';
import { safelyParseJSON } from '@egr/xbox/utils/Json';
import { getEnvBoolean, getEnvVar, PUBLIC_URL } from '@egr/xbox/utils/ReactScriptHelper';
import { setupSentry, updateTag, enableSentry } from '@egr/xbox/utils/errors/Error';

import { GatekeeperClient } from '@easterngraphics/gatekeeper-lib';
import { wcfConfig } from '@easterngraphics/wcf/modules/utils';
import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

if (!window.location.hash && !window.location.host.includes('localhost') && !window.location.host.includes('127.0.0.1')) {
    window.location.replace(getEnvVar('REDIRECT_NO_PARAMS', 'https://pcon-solutions.com/pcon-cloud/'));
}

printTimer('Start', 'START');
printTimer('BeforeConfiguration', 'START');

enableSentry(true);
setupSentry();

function getEvaluationLicensedInformation(): boolean {
    const allowedDomains: Array<RegExp> = [
        /(https?:\/\/(.+?\.)?pcon\.eu)/,
        /(https?:\/\/(.+?\.)?easterngraphics\.com)/,
        /(https?:\/\/(.+?\.)?pcon-solutions\.com)/
    ];
    return allowedDomains.some((value) => { return value.test(EAIWS_SERVER ?? ''); });
}

async function run(): Promise<void> {
    wcfConfig.dataPath = PUBLIC_URL + '/w-cf/data/';

    let gatekeeperId: string = DisableCloud ? '' : GATEKEEPER_ID;
    if (!ALLOW_CUSTOM_POOL && gatekeeperId.indexOf(':') !== -1) {
        gatekeeperId = gatekeeperId.split(':')[0];
    }

    const props = await getProps(gatekeeperId);

    const i18nextInstance = await initInstance(
        USER_LANGUAGES,
        props.uiConfig?.settings.general.lang
    );

    if (isNotNullOrEmpty(props.uiConfig?.settings.general.translationsImportUrl)) {
        const languages = isNotNullOrEmpty(i18nextInstance.language) ? [i18nextInstance.language, 'en'] : ['en'];
        for (const language of languages) {
            try {
                const request = await fetch( `${props.uiConfig.settings.general.translationsImportUrl}/${language}.json`);
                const content = await request.json();
                i18next.addResourceBundle(language, 'translation', content);
            } catch (e) {
                console.error(e);
            }
        }
    }

    const app = (
        <AppLoader
            {...props}
            loadIframeResizer={Boolean(
                !DisableCloud &&
                props.uiConfig?.settings?.general.enableIframeResizer &&
                window.self !== window.top
            )}
        />
    );

    const rootElement = document.getElementById('root');

    if (rootElement == null) {
        throw new Error('root element not found');
    }

    const root = createRoot(rootElement);

    root.render(app);
}

async function getProps(gatekeeperId: string): Promise<AppProps> {
    const isEvaluationLicensed = getEvaluationLicensedInformation();

    updateTag('gatekeeperId', gatekeeperId);

    if (isNotNullOrEmpty(EAIWS_SERVER) && isNotNullOrEmpty(EaiwsStartup) && !isEvaluationLicensed) {
        addGatekeeperIdHook(async () => { return 'null'; /* will cause intended "unlicensed error" */ });
        return {
            gatekeeperId,
            getSessionManager: (app: App) => new GatekeeperManager(app),
        };
    } else {
        await pConServiceProvider.initialize();
        const gatekeeperClient = new GatekeeperClient({
            serviceProvider: pConServiceProvider,
        });
        const UiConfig = await loadUiSettings(gatekeeperId, gatekeeperClient);
        const sessionStorageConfig: UiConfig | null = getSessionStorageStateOverride() as UiConfig | null;
        if (getEnvBoolean('apcs', false) && sessionStorageConfig && UiConfig) {
            UiConfig.settings = sessionStorageConfig.settings;
            UiConfig.visibility = sessionStorageConfig.visibility;
            UiConfig.styles = sessionStorageConfig.styles;
            UiConfig.theme = sessionStorageConfig.theme;
            //merging here to keep the default iframeparents localhost, and egr domains
            UiConfig.permissions = merge(
                UiConfig.permissions,
                sessionStorageConfig.permissions
            );
        }
        if (!UiConfig) {
            addGatekeeperIdHook(async () => { return 'null'; /* will cause intended "unlicensed error" */ });
            return {
                gatekeeperId,
                getSessionManager: (app: App) => new GatekeeperManager(app, undefined, gatekeeperClient),
            };
        } else if (isNotNullOrEmpty(EAIWS_SERVER) && isNotNullOrEmpty(EaiwsStartup)) {
            return {
                gatekeeperId,
                getSessionManager: (app: App) => new StaticEaiwsManager(app),
                uiConfig: UiConfig
            };
        } else {
            addGatekeeperIdHook(async () => { return gatekeeperId; });
            void sendParams();
            return {
                gatekeeperId,
                getSessionManager: (app: App) => new PconUiSessionManager(app),
                uiConfig: UiConfig
            };
        }
    }
}

const getSessionStorageStateOverride = () => {
    const sessionStorageKey: string = GATEKEEPER_ID + '-customSettings';
    try {
        const UiConfig: string | null = sessionStorage.getItem(sessionStorageKey);
        return isNotNullOrEmpty(UiConfig) ? (safelyParseJSON(UiConfig)!) : null;
    } catch {
        return null;
    }
};

void run();